import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Lock, ArrowLeft, FileText, Pencil, X, Check, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { formatDate, formatDateTime, toTagList } from "../lib/format";

const LOCKED_FIELDS = ["candidate_name", "contact_number", "email", "home_city", "domain", "work_experience", "highest_qualification", "logged_by", "date_added"];
const UPDATABLE = ["current_city", "expected_ctc", "current_ctc", "notice_period", "key_skills", "candidate_status", "latest_note", "next_follow_up", "preferred_work_setup", "industry"];

const singleValue = (v) => {
  if (!v) return "";
  if (Array.isArray(v)) return v[0] || "";
  return String(v);
};

const Field = ({ label, value, locked, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</Label>
      {locked && <Lock className="w-3 h-3 text-slate-400" />}
    </div>
    <div className="text-sm text-slate-900 mt-1">{value ?? "—"}</div>
  </div>
);

export default function CandidateProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isSC = user?.role === "super_custodian";
  const [c, setC] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState(["Looking", "Not Looking", "Passive", "Placed", "Follow Up Later"]);
  const [workSetups, setWorkSetups] = useState(["Work from office", "Work from home", "Hybrid"]);

  const load = async () => {
    const { data } = await api.get(`/candidates/${id}`);
    setC(data);
    setForm({
      current_city: data.current_city || "",
      current_ctc: data.current_ctc ?? "",
      expected_ctc: data.expected_ctc ?? "",
      notice_period: data.notice_period ?? "",
      key_skills: data.key_skills || "",
      candidate_status: data.candidate_status || "Looking",
      latest_note: "",
      next_follow_up: data.next_follow_up ? data.next_follow_up.slice(0, 10) : "",
      preferred_work_setup: data.preferred_work_setup || "",
      industry: singleValue(data.industry),
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const [industries, setIndustries] = useState([]);
  useEffect(() => {
    api.get("/meta/dropdowns").then(({ data }) => {
      const s = (data.status || []).map((o) => o.value);
      const w = (data.work_setup || []).map((o) => o.value);
      const i = (data.industry || []).map((o) => o.value);
      setStatuses(s.length ? s : statuses);
      setWorkSetups(w.length ? w : workSetups);
      setIndustries(i);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Ensure stored values are present in dropdown options even if SC removed them.
  const statusOptions = React.useMemo(() => {
    const s = new Set(statuses);
    if (c?.candidate_status) s.add(c.candidate_status);
    return Array.from(s);
  }, [statuses, c?.candidate_status]);
  const workSetupOptions = React.useMemo(() => {
    const s = new Set(workSetups);
    if (c?.preferred_work_setup) s.add(c.preferred_work_setup);
    return Array.from(s);
  }, [workSetups, c?.preferred_work_setup]);
  const industryOptions = React.useMemo(() => {
    const s = new Set(industries);
    const cur = singleValue(c?.industry);
    if (cur) s.add(cur);
    return Array.from(s);
  }, [industries, c?.industry]);

  if (!c) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

  const canEdit = true;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of UPDATABLE) {
        const v = form[k];
        if (k === "latest_note") {
          if (v && v.trim()) payload[k] = v.trim();
          continue;
        }
        // Skip unchanged or blank
        const oldVal = c[k];
        const oldStr = oldVal == null ? "" : String(oldVal);
        const newStr = v == null ? "" : String(v);
        if (newStr === "" || newStr === oldStr) continue;
        if (k === "current_ctc" || k === "expected_ctc") {
          const n = Number(v);
          if (Number.isNaN(n)) { toast.error(`${k} must be a number`); setSaving(false); return; }
          payload[k] = n;
        } else if (k === "notice_period") {
          const n = parseInt(v, 10);
          if (Number.isNaN(n)) { toast.error("Notice period must be a number"); setSaving(false); return; }
          payload[k] = n;
        } else {
          payload[k] = v;
        }
      }
      if (Object.keys(payload).length === 0) {
        toast.message("No changes to save");
        setEditing(false);
        setSaving(false);
        return;
      }
      const { data } = await api.patch(`/candidates/${id}`, payload);
      setC(data);
      setEditing(false);
      toast.success("Candidate details updated successfully");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const uploadResume = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/candidates/${id}/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
  };

  const downloadResume = async (mode) => {
    // DOCX cannot be reliably rendered inline by browsers — fall back to Download
    if (mode === "view" && c.resume_ext && c.resume_ext !== ".pdf") {
      toast.message("Preview not available for Word documents, please use Download");
      return;
    }
    try {
      // First ask the backend whether the file lives remotely (S3) or locally.
      const meta = await api.get(`/candidates/${id}/resume`, {
        params: { url_only: 1, ...(mode === "view" ? { mode: "view" } : {}) },
      });
      if (meta.data?.is_remote && meta.data.url) {
        // S3 (or any presigned-URL backend) — open/download directly in browser.
        if (mode === "view") {
          window.open(meta.data.url, "_blank");
        } else {
          const a = document.createElement("a");
          a.href = meta.data.url; a.download = meta.data.filename || `resume_${c.candidate_name}${c.resume_ext || ""}`;
          document.body.appendChild(a); a.click(); a.remove();
        }
        return;
      }
      // Local storage — stream via authenticated blob fetch (backward-compatible).
      const res = await api.get(`/candidates/${id}/resume`, {
        params: mode === "view" ? { mode: "view" } : {},
        responseType: "blob",
      });
      const mime = res.headers?.["content-type"] || (c.resume_ext === ".pdf" ? "application/pdf" : "application/octet-stream");
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      if (mode === "view") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = c.resume_filename || `resume_${c.candidate_name}${c.resume_ext || ""}`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error("Could not load resume");
    }
  };

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <button onClick={() => nav(-1)} data-testid="back-button" className="text-sm text-slate-500 hover:text-slate-900 mb-4 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="profile-name">{c.candidate_name}</h1>
          <p className="text-slate-500 mt-1">{c.domain || "—"} · {c.work_experience ?? "—"} years experience</p>
          <div className="mt-3 flex items-center gap-3">
            <StatusBadge status={c.candidate_status} />
            <span className="text-xs text-slate-400">Logged by <span className="font-medium text-slate-600">{c.logged_by || "—"}</span></span>
          </div>
        </div>
        <div className="flex gap-2">
          {c.resume_ext ? (
            <>
              <Button data-testid="view-resume-button" variant="outline" onClick={() => downloadResume("view")} className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                <FileText className="w-4 h-4 mr-1.5" />View Resume
              </Button>
              <Button data-testid="download-resume-button" variant="outline" onClick={() => downloadResume("download")}>
                Download
              </Button>
            </>
          ) : c.resume_link ? (
            <a data-testid="view-resume-link" href={c.resume_link} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                <FileText className="w-4 h-4 mr-1.5" />View Resume Link
              </Button>
            </a>
          ) : null}
          <label data-testid="upload-resume-button" className="inline-flex items-center justify-center gap-1.5 h-10 px-4 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50 cursor-pointer">
            <input type="file" accept=".pdf,.docx,.doc" className="hidden" onChange={(e) => uploadResume(e.target.files?.[0])} />
            <FileText className="w-4 h-4" />{c.resume_ext ? "Replace Resume" : "Upload Resume"}
          </label>
          {canEdit && !editing && (
            <Button data-testid="edit-button" onClick={() => setEditing(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Pencil className="w-4 h-4 mr-1.5" />Edit Details
            </Button>
          )}
          {editing && (
            <>
              <Button data-testid="cancel-edit-button" variant="outline" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-1.5" />Cancel</Button>
              <Button data-testid="save-edit-button" disabled={saving} onClick={save} className="bg-indigo-600 hover:bg-indigo-700"><Check className="w-4 h-4 mr-1.5" />Save</Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-5">Profile</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Contact number" value={c.contact_number} locked testid="field-contact" />
            <Field label="Email" value={c.email} locked testid="field-email" />
            <Field label="Domain / Function" value={singleValue(c.domain)} locked testid="field-domain" />
            <Field label="Highest qualification" value={c.highest_qualification} locked testid="field-qualification" />
            <Field label="Home city" value={c.home_city} locked testid="field-home-city" />

            {/* Editable section */}
            {editing ? (
              <>
                <div>
                  <Label>Industry</Label>
                  <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                    <SelectTrigger data-testid="edit-industry" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{industryOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preferred work setup</Label>
                  <Select value={form.preferred_work_setup} onValueChange={(v) => setForm({ ...form, preferred_work_setup: v })}>
                    <SelectTrigger data-testid="edit-setup" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{workSetupOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Current city</Label>
                  <Input data-testid="edit-current-city" value={form.current_city} onChange={(e) => setForm({ ...form, current_city: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Current CTC (LPA)</Label>
                  <Input data-testid="edit-current-ctc" type="number" step="0.1" value={form.current_ctc} onChange={(e) => setForm({ ...form, current_ctc: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Expected CTC (LPA)</Label>
                  <Input data-testid="edit-expected-ctc" type="number" step="0.1" value={form.expected_ctc} onChange={(e) => setForm({ ...form, expected_ctc: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Notice period (days)</Label>
                  <Input data-testid="edit-notice" type="number" value={form.notice_period} onChange={(e) => setForm({ ...form, notice_period: e.target.value })} className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Key skills</Label>
                  <Input data-testid="edit-skills" value={form.key_skills} onChange={(e) => setForm({ ...form, key_skills: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.candidate_status} onValueChange={(v) => setForm({ ...form, candidate_status: v })}>
                    <SelectTrigger data-testid="edit-status" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Next follow up</Label>
                  <Input data-testid="edit-followup" type="date" value={form.next_follow_up?.slice(0, 10) || ""} onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })} className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Add note (appends to history)</Label>
                  <Textarea data-testid="edit-note" rows={3} value={form.latest_note} onChange={(e) => setForm({ ...form, latest_note: e.target.value })} className="mt-1" placeholder="Spoke with candidate, will revert by Friday…" />
                </div>
              </>
            ) : (
              <>
                <Field label="Industry" value={singleValue(c.industry) || "—"} testid="field-industry" />
                <Field label="Preferred work setup" value={c.preferred_work_setup} testid="field-setup" />
                <Field label="Current city" value={c.current_city} testid="field-current-city" />
                <Field label="Current CTC" value={c.current_ctc != null ? `${c.current_ctc} LPA` : "—"} testid="field-current-ctc" />
                <Field label="Expected CTC" value={c.expected_ctc != null ? `${c.expected_ctc} LPA` : "—"} testid="field-expected-ctc" />
                <Field label="Notice period" value={c.notice_period != null ? `${c.notice_period} days` : "—"} testid="field-notice" />
                <Field label="Key skills" value={c.key_skills} testid="field-skills" />
                <Field label="Next follow up" value={c.next_follow_up ? formatDate(c.next_follow_up) : "—"} testid="field-followup" />
                <Field label="Latest note" value={c.latest_note} testid="field-latest-note" />
                {c.follow_up_set_by && (
                  <div className="sm:col-span-2 text-xs text-slate-500" data-testid="field-followup-set-by">
                    Follow-up set by <span className="font-medium text-slate-700">{c.follow_up_set_by}</span>
                    {c.follow_up_set_at && <> on {formatDateTime(c.follow_up_set_at)}</>}
                  </div>
                )}
                <div className="sm:col-span-2" data-testid="field-resume">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Resume</Label>
                  {c.resume_ext ? (
                    <div className="mt-1 text-sm text-slate-700 flex items-center gap-3 flex-wrap">
                      <span>{c.resume_filename || "Resume uploaded"} <span className="text-xs text-slate-400">· Last updated {c.resume_uploaded_at ? formatDate(c.resume_uploaded_at) : "—"}</span></span>
                      {isSC && (
                        <button
                          data-testid="delete-resume-button"
                          onClick={async () => {
                            if (!window.confirm("Delete this resume?")) return;
                            try {
                              await api.delete(`/candidates/${id}/resume`);
                              toast.success("Resume deleted");
                              load();
                            } catch { toast.error("Delete failed"); }
                          }}
                          className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />Delete
                        </button>
                      )}
                    </div>
                  ) : c.resume_link ? (
                    <a href={c.resume_link} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">{c.resume_link}</a>
                  ) : (
                    <div className="mt-1 text-sm text-slate-400">No resume uploaded</div>
                  )}
                </div>
              </>
            )}
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-4">Note history</h3>
          {(!c.note_history || c.note_history.length === 0) && (
            <p className="text-sm text-slate-400">No notes yet.</p>
          )}
          <div className="space-y-3 max-h-[60vh] overflow-y-auto scrollbar-thin pr-2" data-testid="note-history">
            {(c.note_history || []).map((n, i) => (
              <div key={i} className="border-l-2 border-indigo-200 pl-3 py-1">
                <div className="text-xs text-slate-400">{formatDateTime(n.ts)} · <span className="text-slate-600 font-medium">{n.by}</span></div>
                <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{n.note}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
